import React, { createContext, useContext, useMemo, ReactNode, useState, useCallback } from 'react';
import { getRecordTypeNamesAndSteps } from '../selected-object/utils';
import { RecordTypesLabelsAndStepsByNameAndObjectName } from '../types';
import { EntityClicked } from './types';
import { useDispatch } from 'react-redux';
import {
  setActivePageTab,
  DEFAULT_OBJECTS_TAB_ID,
} from '../../../reducers/documentation/documentationPageReducer';

/**
 * This context holds only HIGH LEVEL information and functions
 * that should be delivered to Documentation but are part of other apps
 * eg. canvasFunnelMap
 */

interface DocumentationContextValue {
  focusOnCanvasElements?: (
    elementIds: string[],
    options?: { maxZoom?: number },
    fitAroundFirstStepOnly?: boolean,
  ) => void;

  objectNamesUsedInCanvas: string[];
  recordTypesUsedInCanvasByObjectName: RecordTypesLabelsAndStepsByNameAndObjectName;
  entityClicked: EntityClicked | null;
  setEntityClicked: (entity: EntityClicked | null) => void;
  canvasFunnelMap?: FunnelMap;
}

interface DocumentationContextProviderProps {
  children: ReactNode;
  focusOnCanvasElements?: (
    elementIds: string[],
    options?: { maxZoom?: number },
    fitAroundFirstStepOnly?: boolean,
  ) => void;
  canvasFunnelMap?: FunnelMap;
  crmOrgId: string;
}

const DocumentationContext = createContext<DocumentationContextValue | undefined>(undefined);

export const DocumentationContextProvider: React.FC<DocumentationContextProviderProps> = ({
  children,
  focusOnCanvasElements,
  canvasFunnelMap,
  crmOrgId,
}) => {
  const dispatch = useDispatch();
  const [entityClicked, setEntityClicked] = useState<EntityClicked | null>(null);
  const funnelsData = canvasFunnelMap?.funnelsData;

  const recordTypesUsedInCanvasByObjectName = useMemo(() => {
    return getRecordTypeNamesAndSteps({
      funnelsData: funnelsData,
      crmOrgId,
    });
  }, [funnelsData, crmOrgId]);

  const objectNamesUsedInCanvas = Object.keys(recordTypesUsedInCanvasByObjectName);

  const _setEntityClicked = useCallback(
    (entity: EntityClicked | null) => {
      entity && dispatch(setActivePageTab({ tabId: DEFAULT_OBJECTS_TAB_ID }));
      setEntityClicked(entity);
    },
    [dispatch],
  );

  const value: DocumentationContextValue = useMemo(
    () => ({
      focusOnCanvasElements,
      objectNamesUsedInCanvas,
      recordTypesUsedInCanvasByObjectName,
      setEntityClicked: _setEntityClicked,
      entityClicked,
      canvasFunnelMap,
    }),
    [
      focusOnCanvasElements,
      objectNamesUsedInCanvas,
      recordTypesUsedInCanvasByObjectName,
      _setEntityClicked,
      entityClicked,
      canvasFunnelMap,
    ],
  );

  return <DocumentationContext.Provider value={value}>{children}</DocumentationContext.Provider>;
};

export const useDocumentationContext = () => {
  const context = useContext(DocumentationContext);
  if (context === undefined) {
    throw new Error('useDocumentationContext must be used within a DocumentationContextProvider');
  }
  return context;
};
